import * as React from 'react';
import './PanelSwitch.less';

const animationClass = 'animated-on-select';

interface Props {
    options: { id: string; label: string }[];
    panelsRefs: React.RefObject<Map<string, React.RefObject<HTMLDivElement>>>;
    containerRef: React.RefObject<HTMLDivElement>;
}

const PanelSwitch: React.FC<Props> = ({ options, panelsRefs, containerRef }: Props) => {
    const scrollAnimationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const immediateAnimationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const panelAnimationTimeoutRefs = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const currentActivePanelRef = React.useRef<string | null>(null);
    const scrollListenerRef = React.useRef<((e: Event) => void) | null>(null);

    const applyAnimation = (panelContainer: HTMLDivElement, id: string) => {
        panelContainer.classList.add(animationClass);

        const existingPanelAnimationTimeout = panelAnimationTimeoutRefs.current.get(id);

        if (existingPanelAnimationTimeout) {
            clearTimeout(existingPanelAnimationTimeout);
        }

        const panelAnimationTimeout = setTimeout(() => {
            panelContainer.classList.remove(animationClass);
            panelAnimationTimeoutRefs.current.delete(id);
        }, 500);

        panelAnimationTimeoutRefs.current.set(id, panelAnimationTimeout);

        cleanupScrollListener();
    };

    const cleanupScrollListener = React.useCallback(() => {
        if (scrollListenerRef.current && containerRef.current) {
            containerRef.current.removeEventListener('scroll', scrollListenerRef.current);
            scrollListenerRef.current = null;
        }
    }, [containerRef]);

    const onPanelSwitchClick = (id: string) => {
        const panelContainer = panelsRefs.current?.get(id)?.current;
        const scrollContainer = containerRef.current;

        if (!panelContainer || !scrollContainer) {
            return;
        }

        cleanupScrollListener();

        let scrollTriggered = false;
        currentActivePanelRef.current = id;

        const onScroll = () => {
            scrollTriggered = true;

            if (scrollAnimationTimeoutRef.current) {
                clearTimeout(scrollAnimationTimeoutRef.current);
            }

            scrollAnimationTimeoutRef.current = setTimeout(() => {
                // Only apply animation if this is still the active panel
                if (currentActivePanelRef.current === id) {
                    applyAnimation(panelContainer, id);
                }
            }, 50);
        };

        scrollListenerRef.current = onScroll;
        scrollContainer.addEventListener('scroll', onScroll, { passive: true });

        panelContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });

        if (immediateAnimationTimeoutRef.current) {
            clearTimeout(immediateAnimationTimeoutRef.current);
        }

        immediateAnimationTimeoutRef.current = setTimeout(() => {
            if (!scrollTriggered && currentActivePanelRef.current === id) {
                applyAnimation(panelContainer, id);
            }
        }, 50);
    };

    return (
        <div className="panel-switch custom-scroll">
            {options.map(o => (
                <span key={o.id} className="panel-switch-option" onClick={() => onPanelSwitchClick(o.id)}>
                    {o.label}
                </span>
            ))}
        </div>
    );
};

export default PanelSwitch;
